import React, { useEffect, useState, useCallback } from 'react';
import {
  StyledFooter,
  FooterDivTop,
  LogoWrapper,
  StyledGroupWrapper,
  CategoriesWrapper,
  MainLinksWrapper,
  ContactInfoWrapper,
  ContactInfoMain,
  ContactInfoSocials,
  ContactInfoChild,
  ContactInfoBody,
  FooterTitle,
  StyledFooterLink,
  SocialLinkWrapper,
  IconButtonStyled,
  SocialName,
  ContactInfoSocialsLinks,
  FooterDivBottom,
  FooterBottomLeft,
  FooterBottomRight,
  CopyrightInfo,
  FooterDivNewsLetter,
  AppInfo,
  ContactInfoRegion,
  LinkLogoStyled,
  SecondLineStyled,
  FirstLineStyled,
  MarkdownWrapper,
} from './Footer.style';
import { getStrapiMedia } from '../../../utils/media';
import Link from 'next/link';
import {
  hexToRgb,
  rgbDataURL,
  toKebabCaseWithAccentsCyrillicCJK,
} from 'corporate-utils';

import {
  FacebookIcon,
  InstagramIcon,
  PinterestIcon,
  YouTubeIcon,
  LinkedinIcon,
} from '../../../assets';
import { useRouter } from 'next/router';
import { Category, MegamenuLink } from '../../../models/domain/interfaces';
import { Link as LinkMenuType } from '../../../models/auto-generated-strapi-types/types/components/Link';
import { getAvailableStringFromCategoryByLocale } from '../../../utils/categories';
import { getTranslations } from '../../../utils/localize';
import { defaultLanguage } from 'corporate-types';
import ImageWithFallback from '../../image-with-fallback/ImageWithFallback';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import NewsLetterSection from '../../../sections/news-letter/NewsLetter';
import {
  getCurrentBuildVersion,
  getFEVersion,
  isDevelopmentEnv,
} from '../../../utils/info';
import Markdown from '../../markdown/Markdown';

interface FooterProps {
  logo: any;
  imageInternalLinkPSC: any;
  locationSchema?: any;
  [key: string]: string;
  privacyDisclaimer: any;
  translations: any;
  userCountry: string;
  regulatoryObligations: string;
}

type FooterPropsWithLinks = FooterProps & {
  mainLinks: LinkMenuType[];
  megamenuLinks: MegamenuLink[];
  isPrerenderRequest: boolean;
};

const Footer: React.FC<FooterPropsWithLinks> = (
  props: FooterPropsWithLinks
) => {
  const { locale } = useRouter();
  const [translations, setTranslations] = useState<any>(async () => {
    return await getTranslations(locale ?? defaultLanguage);
  });
  const isDevelopment = isDevelopmentEnv();
  const getTranlationCallback = useCallback(async () => {
    const languages = await getTranslations(locale ?? defaultLanguage);
    setTranslations(languages);
  }, [locale]);

  useEffect(() => {
    getTranlationCallback();
  }, [getTranlationCallback]);

  const getChildrenLinks = (fatherLink: MegamenuLink) => {
    const { text, url, firstChild } = fatherLink;
    const fatherDiv = <FooterTitle>{text}</FooterTitle>;

    const childDivs = firstChild?.map((child: Category, index) =>
      child?.idFather !== 0 ? (
        <div key={index}>
          <Link
            href={`${url}/${toKebabCaseWithAccentsCyrillicCJK(
              getAvailableStringFromCategoryByLocale(
                child,
                locale,
                'packages/corporate-ui/src/components/shell/footer-fls/Footer.tsx'
              )
            )}`}
            passHref
          >
            <StyledFooterLink>
              {getAvailableStringFromCategoryByLocale(
                child,
                locale,
                'packages/corporate-ui/src/components/shell/footer-fls/Footer.tsx'
              )}
            </StyledFooterLink>
          </Link>
        </div>
      ) : (
        <></>
      )
    );

    return (
      <StyledGroupWrapper>
        {fatherDiv}
        {childDivs}
      </StyledGroupWrapper>
    );
  };

  return (
    <>
      <FooterDivNewsLetter>
        <NewsLetterSection
          data={{
            id: 0,
            mainTitle: props?.newsTitle,
            subTitle: props?.newsParagraph,
            privacyText: props?.privacyDisclaimer,
          }}
          translations={translations}
          userCountry={props?.userCountry}
        />
      </FooterDivNewsLetter>
      <StyledFooter>
        <FooterDivTop>
          <CategoriesWrapper>
            {props?.megamenuLinks?.map((link) => {
              return getChildrenLinks(link);
            })}
          </CategoriesWrapper>
          <MainLinksWrapper>
            <StyledGroupWrapper>
              <FooterTitle>
                {translations?.components?.footer?.sectionTitles?.pages}
              </FooterTitle>
              {props?.mainLinks &&
                props?.mainLinks?.map((link) => {
                  return !link?.subLink || !link?.subLink.length ? (
                    <Link href={link?.url || ''} passHref>
                      <StyledFooterLink>{link?.text}</StyledFooterLink>
                    </Link>
                  ) : (
                    link?.subLink.map((subL) => (
                      <Link href={subL?.url || ''} passHref>
                        <StyledFooterLink>
                          {link?.text + ' - ' + subL?.text}
                        </StyledFooterLink>
                      </Link>
                    ))
                  );
                })}
            </StyledGroupWrapper>
          </MainLinksWrapper>
          <ContactInfoWrapper>
            <ContactInfoMain>
              <FooterTitle>
                {translations?.components?.footer?.sectionTitles?.contactInfo}
              </FooterTitle>
              <ContactInfoBody>
                <ContactInfoChild>{props?.companyTitle}</ContactInfoChild>
              </ContactInfoBody>
              <ContactInfoBody>
                <ContactInfoChild>{props?.companyAddress}</ContactInfoChild>
              </ContactInfoBody>
              <ContactInfoBody>
                <ContactInfoChild>
                  {props?.companyPhoneTitle} {props?.companyPhoneValue}
                </ContactInfoChild>
                <ContactInfoChild>
                  {props?.companyFaxTitle} {props?.companyFaxValue}
                </ContactInfoChild>
                <ContactInfoChild>{props?.companyEmailValue}</ContactInfoChild>
              </ContactInfoBody>
              <ContactInfoBody>
                <ContactInfoChild>{props?.companyPiva}</ContactInfoChild>
                <ContactInfoChild>{props?.companyRegImp}</ContactInfoChild>
                <ContactInfoChild>{props?.companyRea}</ContactInfoChild>
                <ContactInfoChild>{props?.companyCapSoc}</ContactInfoChild>
              </ContactInfoBody>
              <AppInfo
                title={translations?.components?.footer?.applicationInfo}
              >
                {getFEVersion()} - {getCurrentBuildVersion()}
                {isDevelopment && ` - ${isDevelopment}`}
              </AppInfo>
            </ContactInfoMain>
            <ContactInfoSocials>
              <FooterTitle>
                {translations?.components?.footer?.sectionTitles?.socials}
              </FooterTitle>
              <ContactInfoSocialsLinks>
                {props?.facebookUrl && (
                  <SocialLinkWrapper>
                    <IconButtonStyled href={props?.facebookUrl}>
                      <SocialName lang="en">Facebook</SocialName>
                      <FacebookIcon fontSize="large" />
                    </IconButtonStyled>
                  </SocialLinkWrapper>
                )}
                {props?.instagramUrl && (
                  <SocialLinkWrapper>
                    <IconButtonStyled href={props?.instagramUrl}>
                      <SocialName lang="en">Instagram</SocialName>
                      <InstagramIcon fontSize="large" />
                    </IconButtonStyled>
                  </SocialLinkWrapper>
                )}
                {props?.youtubeUrl && (
                  <SocialLinkWrapper>
                    <IconButtonStyled href={props?.youtubeUrl}>
                      <SocialName lang="en">YouTube</SocialName>
                      <YouTubeIcon fontSize="large" />
                    </IconButtonStyled>
                  </SocialLinkWrapper>
                )}
                {props?.pinterestUrl && (
                  <SocialLinkWrapper>
                    <IconButtonStyled href={props?.pinterestUrl}>
                      <SocialName lang="en">Pinterest</SocialName>
                      <PinterestIcon fontSize="large" />
                    </IconButtonStyled>
                  </SocialLinkWrapper>
                )}
                {props?.linkedinUrl && (
                  <SocialLinkWrapper>
                    <IconButtonStyled href={props?.linkedinUrl}>
                      <SocialName lang="en">LinkedIn</SocialName>
                      <LinkedinIcon fontSize="large" />
                    </IconButtonStyled>
                  </SocialLinkWrapper>
                )}
              </ContactInfoSocialsLinks>
            </ContactInfoSocials>

            <ContactInfoRegion>
              {props?.imageInternalLinkPSC?.image &&
                props?.imageInternalLinkPSC?.page && (
                <Link
                  href={`/${props?.imageInternalLinkPSC?.page?.data?.attributes?.slug}`}
                  passHref
                >
                  <LinkLogoStyled>
                    <ImageWithFallback
                      isPrerenderRequest={props?.isPrerenderRequest}
                      style={{ width: 'inherit', position: 'relative' }}
                      layout="responsive"
                      objectFit="contain"
                      placeholder="blur"
                      blurDataURL={rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )}
                      src={getStrapiMedia(
                        props?.imageInternalLinkPSC?.image?.data?.attributes
                          ?.url ?? ''
                      )}
                      width={
                        props?.imageInternalLinkPSC?.image?.data?.attributes
                          ?.width
                      }
                      height={
                        props?.imageInternalLinkPSC?.image?.data?.attributes
                          ?.height
                      }
                      alt={
                        props?.imageInternalLinkPSC?.image?.data?.attributes
                          ?.alternativeText
                      }
                      fallbackSrc={DEFAULT_IMAGE}
                    />
                  </LinkLogoStyled>
                </Link>
              )}
            </ContactInfoRegion>
          </ContactInfoWrapper>
        </FooterDivTop>
        <FooterDivBottom>
          <FooterBottomLeft>
            <LogoWrapper>
              <ImageWithFallback
                layout="fill"
                objectFit="contain"
                placeholder="blur"
                blurDataURL={rgbDataURL(
                  hexToRgb('ffffff')?.[0],
                  hexToRgb('ffffff')?.[1],
                  hexToRgb('ffffff')?.[2]
                )}
                src={getStrapiMedia(props?.logo?.data?.attributes?.url ?? '')}
                alt={props?.logo?.data?.attributes?.alternativeText}
                fallbackSrc={DEFAULT_IMAGE}
                isPrerenderRequest={props?.isPrerenderRequest}
              />
            </LogoWrapper>
            {props?.copyrightInfo && (
              <CopyrightInfo>
                {props?.copyrightInfo}
                <br />
                {`Copyright © ${
                  props?.companyTitle
                } a socio unico - ${new Date().getFullYear()}`}
              </CopyrightInfo>
            )}
          </FooterBottomLeft>
          <FooterBottomRight>
            <FirstLineStyled>
              {props?.termsAndConditionsLink && (
                <Link href={props?.termsAndConditionsLink} passHref>
                  <StyledFooterLink>
                    {props?.termsAndConditionsText || 'Terms and conditions'}
                  </StyledFooterLink>
                </Link>
              )}
              {props?.privacyLink && (
                <Link href={props?.privacyLink} passHref>
                  <StyledFooterLink>
                    {props?.privacyText || 'Privacy'}
                  </StyledFooterLink>
                </Link>
              )}
              {props?.customerCareLink && (
                <Link href={props?.customerCareLink} passHref>
                  <StyledFooterLink>
                    {props?.customerCareText || 'Customer care'}
                  </StyledFooterLink>
                </Link>
              )}
            </FirstLineStyled>
            {props?.regulatoryObligations ? (
              <SecondLineStyled>
                <MarkdownWrapper>
                  <Markdown children={props?.regulatoryObligations}></Markdown>
                </MarkdownWrapper>
              </SecondLineStyled>
            ) : (
              <></>
            )}
          </FooterBottomRight>
        </FooterDivBottom>
      </StyledFooter>
    </>
  );
};

export default Footer;
